/**
 * Marketing attribution capture.
 *
 * Captures UTM params, click IDs (fbclid/gclid), referrer and landing path
 * on every navigation that carries new attribution signals. Persists both
 * first-touch (write once) and last-touch (overwritten) in localStorage
 * with a ~30 day TTL.
 *
 * This data is internal first-party attribution — independent of any
 * marketing consent (no third-party scripts involved). At conversion time
 * a snapshot is built (`buildAttributionPayload`) and sent to the backend
 * inside the ProjectInfo POST body.
 */

const STORAGE_KEY = 'manito_attribution'
const SCHEMA_VERSION = 1
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export interface TouchPoint {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  fbclid?: string
  gclid?: string
  referrer?: string
  landing_path?: string
  ts: string
}

interface AttributionState {
  version: number
  first_touch: TouchPoint | null
  last_touch: TouchPoint | null
}

export interface AttributionPayload {
  schema_version: number
  first_touch: TouchPoint | null
  last_touch: (TouchPoint & { fbc?: string; fbp?: string }) | null
  conversion: { event_id: string }
  consent: { marketing: boolean; analytics: boolean }
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const CLICK_ID_KEYS = ['fbclid', 'gclid'] as const

const MAX_VALUE_LENGTH = 512

function clip(value: string): string {
  return value.length > MAX_VALUE_LENGTH ? value.slice(0, MAX_VALUE_LENGTH) : value
}

function loadState(): AttributionState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AttributionState
    if (parsed.version !== SCHEMA_VERSION) return null
    // TTL: expire based on first_touch timestamp
    const anchor = parsed.first_touch?.ts || parsed.last_touch?.ts
    if (anchor && Date.now() - new Date(anchor).getTime() > TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function saveState(state: AttributionState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore (private mode / quota)
  }
}

/** Strip query string + hash from a URL to avoid storing PII-laden params. */
function cleanReferrer(referrer: string): string | undefined {
  if (!referrer) return undefined
  try {
    const url = new URL(referrer)
    // Ignore internal referrers — only external sources matter
    if (url.hostname === window.location.hostname) return undefined
    return clip(`${url.origin}${url.pathname}`)
  } catch {
    return undefined
  }
}

/** Build a touch point from the current URL/referrer. Null when no signal. */
function captureTouchPoint(): TouchPoint | null {
  const params = new URLSearchParams(window.location.search)

  const touch: TouchPoint = { ts: new Date().toISOString() }
  let hasSignal = false

  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) {
      touch[key] = clip(value)
      hasSignal = true
    }
  }
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key)
    if (value) {
      touch[key] = clip(value)
      hasSignal = true
    }
  }

  const referrer = cleanReferrer(document.referrer)
  if (referrer) {
    touch.referrer = referrer
    hasSignal = true
  }

  if (!hasSignal) return null

  touch.landing_path = clip(window.location.pathname)
  return touch
}

/**
 * Capture attribution from the current URL. Call once on app boot
 * (before first navigation completes). Idempotent per page load.
 */
let captured = false
export function captureAttribution(): void {
  if (captured) return
  captured = true

  const touch = captureTouchPoint()
  if (!touch) return

  const state: AttributionState = loadState() ?? {
    version: SCHEMA_VERSION,
    first_touch: null,
    last_touch: null,
  }

  if (!state.first_touch) {
    state.first_touch = touch
  }
  state.last_touch = touch
  saveState(state)
}

/** Read a cookie value by name. */
function readCookie(name: string): string | undefined {
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(name + '='))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined
}

/** Meta `fbc` parameter built from a stored fbclid: fb.1.<ts>.<fbclid> */
function buildFbc(touch: TouchPoint | null): string | undefined {
  if (!touch?.fbclid) return undefined
  const ts = new Date(touch.ts).getTime() || Date.now()
  return `fb.1.${ts}.${touch.fbclid}`
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Fallback (older browsers)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** Current stored attribution (or null when none / expired). */
export function getAttribution(): { first_touch: TouchPoint | null; last_touch: TouchPoint | null } {
  const state = loadState()
  return {
    first_touch: state?.first_touch ?? null,
    last_touch: state?.last_touch ?? null,
  }
}

/**
 * Build conversion-time attribution payload for the ProjectInfo POST.
 * Generates the shared event_id used for browser Pixel ↔ CAPI dedup.
 */
export function buildAttributionPayload(consent: {
  marketing: boolean
  analytics: boolean
}): AttributionPayload {
  const { first_touch, last_touch } = getAttribution()

  const fbc = buildFbc(last_touch)
  const fbp = readCookie('_fbp')

  return {
    schema_version: SCHEMA_VERSION,
    first_touch,
    last_touch: last_touch
      ? { ...last_touch, ...(fbc ? { fbc } : {}), ...(fbp ? { fbp } : {}) }
      : null,
    conversion: { event_id: uuid() },
    consent,
  }
}

export function useAttribution() {
  return {
    captureAttribution,
    getAttribution,
    buildAttributionPayload,
  }
}
